/* EV1 coupon preview uses an isolated Storefront cart, not the customer's
 * storefront cart. No account, checkout, redemption or URL amount is used. */
(() => {
  'use strict';
  const query = `mutation ProductCouponPreview($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {
    cartCreate(input: $input) {
      userErrors { message }
      cart {
        discountCodes { code applicable }
        lines(first: 2) { nodes {
          quantity merchandise { ... on ProductVariant { id } }
          cost { amountPerQuantity { amount currencyCode } totalAmount { amount currencyCode } }
          discountAllocations { discountedAmount { amount currencyCode } ... on CartCodeDiscountAllocation { code } }
        } }
        discountAllocations { discountedAmount { amount currencyCode } ... on CartCodeDiscountAllocation { code } }
      }
    }
  }`;
  function selectedCode(search, cookie, stored = '') {
    const params = new URLSearchParams(search);
    if (params.has('discount')) return (params.get('discount') || '').trim();
    const match = cookie.split(';').find(item => item.trim().startsWith('discount_code='));
    if (match) {
      try { return decodeURIComponent(match.trim().slice('discount_code='.length)).trim(); }
      catch (_) { return ''; }
    }
    return String(stored || '').trim();
  }
  function cents(money, currency) {
    if (!money || money.currencyCode !== currency || !/^\d+(?:\.\d{1,2})?$/.test(String(money.amount))) return null;
    const value = Math.round(Number(money.amount) * 100);
    return Number.isSafeInteger(value) ? value : null;
  }
  function parseQuote(payload, { code, currency, variant }) {
    const result = payload?.data?.cartCreate;
    if (payload?.errors?.length || !result || result.userErrors?.length) return null;
    const cart = result.cart;
    const matches = value => typeof value === 'string' && value.toUpperCase() === code.toUpperCase();
    if (!cart?.discountCodes?.some(item => matches(item.code) && item.applicable === true)) return null;
    const lines = cart.lines?.nodes;
    if (!Array.isArray(lines) || lines.length !== 1) return null;
    const line = lines[0];
    if (line.quantity !== 1 || line.merchandise?.id !== 'gid://shopify/ProductVariant/' + variant.id) return null;
    const original = cents(line.cost?.amountPerQuantity, currency);
    let price = cents(line.cost?.totalAmount, currency);
    if (original === null || price === null || original !== variant.price) return null;
    let codeSavings = 0;
    for (const allocation of line.discountAllocations || []) {
      const amount = cents(allocation.discountedAmount, currency);
      if (amount === null) return null;
      if (matches(allocation.code)) codeSavings += amount;
    }
    // With exactly one item, order-level allocations all belong to this item.
    for (const allocation of cart.discountAllocations || []) {
      const amount = cents(allocation.discountedAmount, currency);
      if (amount === null) return null;
      price -= amount;
      if (matches(allocation.code)) codeSavings += amount;
    }
    if (!codeSavings || !Number.isSafeInteger(price) || price < 0 || price >= original) return null;
    return { original, price };
  }
  async function quotePrice(fetcher, context) {
    const response = await fetcher('/api/2026-10/graphql.json', {
      method: 'POST', credentials: 'omit', mode: 'same-origin',
      headers: { 'Content-Type': 'application/json' }, signal: AbortSignal.timeout(12000),
      body: JSON.stringify({ query, variables: { country: context.country, input: {
        lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + context.variant.id, quantity: 1 }],
        discountCodes: [context.code], buyerIdentity: { countryCode: context.country }
      } } })
    });
    if (!response.ok) return null;
    return parseQuote(await response.json(), context);
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { selectedCode, parseQuote, quotePrice };
  if (typeof document === 'undefined') return;
  function start() {
    const configNode = document.querySelector('[data-product-coupon-config]');
    if (!configNode || window.productCouponPrice) return;
    const config = JSON.parse(configNode.textContent);
    const container = document.getElementById(config.priceId);
    if (!container || !['USD', 'GBP', 'EUR'].includes(config.currency) || !/^[A-Z]{2}$/.test(config.country)) return;
    let stored = '';
    try { stored = sessionStorage.getItem('points-selected-discount') || ''; } catch (_) { /* Optional. */ }
    const code = selectedCode(location.search, document.cookie, stored);
    if (!code || code.includes(',')) return;
    // /discount/CODE leaves a readable cookie. Preserve it for URL sharing.
    const url = new URL(location.href);
    if (!url.searchParams.has('discount')) {
      url.searchParams.set('discount', code);
      history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    }
    let variant = config.variants.find(item => String(item.id) === String(config.variantId));
    let normalHTML = container.innerHTML;
    let quote = null;
    let revision = 0;
    let pendingKey = '';
    function render() {
      if (!container.isConnected) return;
      if (!quote) {
        if (container.dataset.couponPreview === 'true') container.innerHTML = normalHTML;
        delete container.dataset.couponPreview;
        return;
      }
      container.innerHTML = normalHTML;
      const price = container.querySelector('.p-price-current .p-price-inline');
      if (!price) return;
      const format = value => new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: config.currency
      }).format(value / 100);
      const taxNote = price.querySelector('.price-tax-note');
      price.replaceChildren(document.createTextNode(format(quote.price)));
      if (taxNote) price.append(taxNote);
      let original = container.querySelector('.p-price-original');
      if (!original) {
        original = document.createElement('div'); original.className = 'p-price-original';
        price.closest('.p-price-current').after(original);
      }
      const originalPrice = document.createElement('s'); originalPrice.className = 'p-price-inline';
      originalPrice.textContent = format(quote.original); original.replaceChildren(originalPrice);
      const label = document.createElement('div'); label.dataset.couponPriceLabel = '';
      label.textContent = config.label; label.style.overflowWrap = 'anywhere'; original.after(label);
      container.dataset.couponPreview = 'true';
    }
    function refresh() {
      if (!variant || !container.isConnected) return;
      const key = String(variant.id);
      if (pendingKey === key) return;
      const request = ++revision;
      pendingKey = key;
      quote = null; render();
      quotePrice(window.fetch.bind(window), { code, currency: config.currency, country: config.country, variant })
        .then(result => { if (request === revision) { quote = result; render(); } })
        .catch(() => { /* Failed/unavailable quotes keep the normal price. */ })
        .finally(() => { if (request === revision) pendingKey = ''; });
    }
    window.productCouponPrice = {
      update(target, selected) {
        if (target !== container) return;
        const changed = String(variant?.id) !== String(selected?.id);
        variant = selected;
        normalHTML = container.innerHTML;
        delete container.dataset.couponPreview;
        if (changed) { quote = null; refresh(); }
        else render();
      }
    };
    window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
    document.addEventListener('visibilitychange', () => { if (!document.hidden) refresh(); });
    refresh();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
